































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import OzOverlay from 'library/v4/components/OzOverlay.vue'

@Component({ components: { OzOverlay } })
export default class OzCenterModal extends Vue {
  $refs!: {
    modalBody: HTMLDivElement
    modalContainer: HTMLDivElement
    modalContainerInner: HTMLDivElement
    modalContainerInnerWrapper: HTMLDivElement
  }

  /**
   * Current windowHeight, used to compute height-related measurement
   */
  @Prop({ required: true }) windowHeight!: number
  /**
   * To force full size modal
   */
  @Prop() fullSize!: boolean
  /**
   * Desktop modal minimum height
   * If number is provided, unit of height will be in px e.g. :min-height="100" -> minHeight will be 100px
   * If string is provided, unit must be provided e.g. :min-height="'100rem'" -> minHeight will be 100rem
   */
  @Prop({ default: 0 }) minHeight!: number | string
  /**
   * Starting click location based relative to window. To achieve zoom from click animation
   *
   * Type signature is `{ top: number; left: number }`
   */
  @Prop({ default: undefined }) startingClickLocation!: { top: number; left: number } | undefined
  /**
   *  Tailwindcss width class
   *
   *  - `w-180` is for the content picker
   *  - `w-128` is for the reaction edit modal
   *  - `w-82` is the default for all screens -- 328px = 360px - (16px * 2)
   *
   *  This allows us to use the same width for all devices.
   */
  @Prop({ default: 'w-180' }) widthClass!: 'w-180' | 'w-128' | 'w-82'
  /**
   *  Tailwindcss shadow class
   *
   *  - `shadow-z5` is the default
   *  - `shadow-elevation-4` is for the content picker when using new shadows
   */
  @Prop({ default: 'shadow-z5' }) shadowClass!: 'shadow-z5' | 'shadow-elevation-4'
  /**
   * Set dark mode
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'
  /**
   * Determines whether the modal should fade in when opened
   */
  @Prop({ default: true }) shouldFadeIn!: boolean

  // tablet/desktop
  minRenderHeight = '0px'

  @Watch('minHeight')
  @Watch('fullSize', { immediate: true })
  handleResize(): void {
    // For tablet/desktop, we simply change the modal height
    this.$nextTick(() => {
      if (this.fullSize) {
        this.minRenderHeight = this.windowHeight - 112 + 'px'
      } else {
        if (typeof this.minHeight === 'number') {
          this.minRenderHeight = this.minHeight + 'px'
        } else if (typeof this.minHeight === 'string') {
          this.minRenderHeight = this.minHeight
        }
      }
    })
    return
  }

  animateIn(): void {
    const modalContainer = this.$refs.modalContainer
    if (!this.startingClickLocation) {
      modalContainer.style.animationName = 'modal-fade' // animation-name: modal-fade;
      return
    }

    const centerOfScreen = {
      top: this.windowHeight / 2,
      left: window.innerWidth / 2,
    }
    const startOffset = {
      top: this.startingClickLocation.top - centerOfScreen.top,
      left: this.startingClickLocation.left - centerOfScreen.left,
    }
    const startTransformation = {
      scale: 0,
      translate3d: {
        y: startOffset.top,
        x: startOffset.left,
        z: 0,
      },
    }
    const endTransformation = {
      scale: 1,
      translate3d: { x: 0, y: 0, z: 0 },
    }

    function generateTransformString(details: {
      scale: number
      translate3d: { x: number; y: number; z: number }
    }): string {
      return `translate3d(${details.translate3d.x}px, ${details.translate3d.y}px, ${details.translate3d.z}px) scale(${details.scale}) `
    }

    modalContainer.style.transition = 'none'
    modalContainer.style.transformOrigin = 'center'
    modalContainer.style.transform = generateTransformString(startTransformation)
    setTimeout(() => {
      /**
       * Decelerated easing for incoming element
       * @see https://material.io/design/motion/speed.html#easing
       */
      modalContainer.style.transition = 'all 300ms cubic-bezier(0.0, 0.0, 0.2, 1)'
      modalContainer.style.transform = generateTransformString(endTransformation)
    }, 1)
  }

  mounted(): void {
    this.animateIn()
  }

  scrimClick(): void {
    /**
     * Emitted with overlay scrim is clicked. Can be used to trigger closure of drawer.
     */
    this.$emit('scrim-click')
  }

  scrimEsc(): void {
    /**
     * Emitted when Escape key is pressed. Can be used to trigger closure of drawer.
     */
    this.$emit('scrim-esc')
  }
}
